import React from "react";
import { createPortal } from "react-dom";
import { ToastContainer, Slide } from "react-toastify";
import styled from "styled-components";
import {useBoundStore} from "@maxis/client-logic/store/useBoundStore";

/* Blocks interaction while a request is in flight.
 *
 * It has to sit ABOVE the popup, not below it. At the old 9998 it lost to popup.css
 * (z-index 10000), so during a save the overlay covered the whole page except the one
 * thing that mattered -- the popup holding the Save button -- and a second click went
 * straight through. Measured: elementFromPoint over the Save button returned the button
 * with a 9998 overlay, and the overlay only at 20002.
 *
 * MessageBox (20001) is a native <dialog> opened with showModal(), so it lives in the
 * browser's top layer and stays clickable regardless of this value -- error alerts and
 * confirms are not blocked by this overlay.
 *
 * This is feedback, not the guarantee: it cannot block the keyboard. The actual
 * duplicate-write guard is the in-flight lock in useFetch.
 */
const OverlayStyle = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 20002;
    pointer-events: auto;

    /* Blocking starts the moment this mounts, but the dimming waits 150ms.
       Requests that finish quickly are the common case, and without the delay every
       one of them flashed the screen dark for a frame or two. The element is here and
       swallowing clicks the whole time either way. */
    opacity: 0;
    animation: loadingBackdropIn 0.18s ease-out 0.15s forwards;

    @keyframes loadingBackdropIn {
        to {
            opacity: 1;
        }
    }
`;




const Toast = () => {
    const loading = useBoundStore((state) => state.toastAction.isLoading);

    /* Notifications must sit above everything they can sit above.
     *
     * At 9999 they lost to popup.css (z-index 10000), so any toast raised while a popup
     * was open rendered behind it and could not be read. 20003 puts them over the popup
     * and over the loading overlay (20002) too, so a message during a save is still
     * legible.
     *
     * One thing this cannot beat: MessageBox is a native <dialog> opened with
     * showModal(), which lives in the browser's top layer, above every z-index. While an
     * alert or confirm is open, a toast stays behind it no matter what value goes here. */
    const toastContainerStyle = (loading) => ({
        zIndex: 20003,
        pointerEvents: "none",
    });

    return createPortal(
        <>
            {loading && <OverlayStyle />}
            <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                transition={Slide}
                style={toastContainerStyle(loading)}
                toastStyle={{ pointerEvents: "none" }}

            />
        </>,
        document.body
    );
};

Toast.displayName = "Toast";

export default Toast;
